// src/api/healthcare/PractitionerWorkflowApi.ts

import { ENV } from '../../config/runtimeEnv';
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

export interface PractitionerWorkflowOverride {
	id: number;
	practitioner_id: UserUuid;
	appointment_type_id: number;
	workflow_template_id: number;
	is_active: boolean;
	auto_generate: boolean;
	created_at: string;
	updated_at: string;
	appointment_type?: any;
	workflow_template?: any;
}

/**
 * Get all workflow overrides for a practitioner
 */
export const fetchPractitionerWorkflowOverrides = async (
	practitionerId: UserUuid
): Promise<PractitionerWorkflowOverride[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-overrides`,
		{
			params: { practitioner_id: practitionerId },
		}
	);
	return response.data.data || [];
};

/**
 * Get workflow override for specific practitioner and appointment type
 */
export const fetchPractitionerWorkflowOverride = async (
	practitionerId: UserUuid,
	appointmentTypeId: number
): Promise<PractitionerWorkflowOverride | null> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-overrides/show`,
		{
			params: {
				practitioner_id: practitionerId,
				appointment_type_id: appointmentTypeId,
			},
		}
	);
	return response.data.data;
};

/**
 * Create or update workflow override
 */
export const savePractitionerWorkflowOverride = async (
	data: Partial<PractitionerWorkflowOverride>
): Promise<PractitionerWorkflowOverride> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/workflow-overrides`,
		data
	);
	return response.data.data;
};

/**
 * Update existing override
 */
export const updatePractitionerWorkflowOverride = async (
	id: number,
	data: Partial<PractitionerWorkflowOverride>
): Promise<PractitionerWorkflowOverride> => {
	const response = await http.put(
		`${API_BASE_URL}/practitioner/workflow-overrides/${id}`,
		data
	);
	return response.data.data;
};

/**
 * Delete workflow override
 */
export const deletePractitionerWorkflowOverride = async (
	id: number
): Promise<void> => {
	await http.delete(
		`${API_BASE_URL}/practitioner/workflow-overrides/${id}`
	);
};

/**
 * Get available workflows
 */
export const fetchAvailableWorkflowsForPractitioner = async (
	company: string
): Promise<any[]> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-overrides/available-workflows`,
		{
			params: { company },
		}
	);
	return response.data.data || [];
};

/**
 * Get all overrides for an appointment type
 */
export const fetchOverridesByAppointmentType = async (
	appointmentTypeId: number
): Promise<Record<UserUuid, PractitionerWorkflowOverride[]>> => {
	const response = await http.get(
		`${API_BASE_URL}/practitioner/workflow-overrides/by-appointment-type/${appointmentTypeId}`
	);
	return response.data.data || {};
};